/**
 * Short local blog posts. Each post links to the deeper guides/service pages and
 * ends at an enquiry form. Posts in DRAFT_POST_SLUGS are noindexed and left out
 * of the blog list's live view, sitemap and navigation until the firm approves them.
 */
import type { LinkProps } from "@tanstack/react-router";
import goldenBeachImage from "@/assets/golden-beach-hero.jpg";
import conveyancingImage from "@/assets/guide-conveyancing.jpg";
import { SELLER_DISCLOSURE_START_LABEL } from "./seller-disclosure";

export type BlogCategory = "Conveyancing tips" | "Local community" | "Wills" | "Probate" | "Legal curiosities" | "Business & leases";

export type BlogPost = {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  date: string; // ISO date
  lastmod?: string; // Recorded significant content revision
  category: BlogCategory;
  area: string;
  intro: string;
  sections: { heading: string; paragraphs: string[]; bullets?: string[] }[];
  links: { to: NonNullable<LinkProps["to"]>; label: string; params?: Record<string, string> }[];
  cta: string;
  author?: string;
  editorial?: boolean;
  disclaimer?: string;
  image?: { src: string; alt: string };
  sources?: { label: string; url: string }[];
};

export const BLOG_POSTS: BlogPost[] = [
{
  "slug": "before-you-spend-money-shop-fit-out-queensland",
  "title": "Before you spend money on a shop fit-out: does the lease protect your investment?",
  "metaTitle": "Shop Fit-out & Lease Risks in Queensland",
  "description": "Michael Klein explains fit-out permissions, rent-free costs, options, make-good and guarantees before a Queensland shop tenant commits money.",
  "date": "2026-11-04",
  "category": "Business & leases",
  "area": "Caloundra and Sunshine Coast",
  "author": "Michael Klein",
  "intro": "You have found the shop. You can picture the counter, the signage and where everything will go. Before you pay a deposit, there is a less exciting question worth answering: does the lease give you what you think you are getting?",
  "sections": [
    {
      "heading": "Make sure the paperwork matches the deal",
      "paragraphs": [
        "Start with what you agreed, then compare it with the proposed lease and disclosure documents. Is the rent-free period actually included? Are all the renewal options there? Do the rent increases match the discussions?",
        "If an email promises one thing and the lease says another, have the difference resolved before signing. Do not assume the agent's explanation will fix an inconsistent clause later.",
        "Check the basics as well: the correct tenant, the correct premises and the space you are entitled to use. If your company is meant to take the lease, putting your own name on it is not a minor detail. Storage, parking and outdoor areas should not be left to assumptions."
      ]
    },
    {
      "heading": "“Rent-free” does not necessarily mean cost-free",
      "paragraphs": [
        "A fit-out period without base rent can still carry other charges. Ask when outgoings, utilities, insurance obligations and other payments begin, and whether the incentive depends on conditions you must meet.",
        "The same applies to “gross rent”. What is included, and what can still be billed separately? Water charges or other extras can change the budget.",
        "For a lease covered by Queensland's Retail Shop Leases Act 1994, statutory restrictions apply to recoverable outgoings. A charge does not become payable simply because someone puts it in a list. Land tax, capital costs, sinking-fund contributions and insurance allocations warrant careful checking rather than being accepted at face value.",
        "Build your budget from the actual documents and estimates, not just the advertised rent."
      ]
    },
    {
      "heading": "Get the fit-out permissions sorted before ordering",
      "paragraphs": [
        "Check what the landlord must approve, which plans are required, who can do the work and whether the centre has its own fit-out rules. Confirm what the landlord will provide at handover, including the condition and capacity of relevant services.",
        "Landlord consent is not necessarily building, planning, fire-safety or other required approval. Your proposed business use and works may need separate checks.",
        "Be especially careful with early access, preliminary agreements and deposits. Do not assume that leaving the formal lease unsigned means nothing is binding. Have any conditions, expiry dates, refund rights and permission to start work checked before committing money."
      ]
    },
    {
      "heading": "An option is not quite the same as extra years",
      "paragraphs": [
        "A renewal option may help you recover the cost of a fit-out, but only if it exists in the final documents and you exercise it properly.",
        "Check the notice window, notice method and any conditions. Also check how the rent will be reviewed during the initial term and on renewal. An option does not necessarily preserve today's rent.",
        "If the lease and disclosure statement describe different options, resolve that mismatch. Then diary the actual dates. An informal conversation about staying on is not a safe substitute for the required notice."
      ]
    },
    {
      "heading": "Work out who owns the fit-out—and who removes it",
      "paragraphs": [
        "You might be taking over a shop with counters, partitions or equipment already installed. That does not tell you who owns them, whether you may use them or who must remove them later.",
        "Read the make-good clause before adding your own improvements. Depending on the documents and applicable law, the obligation may reach beyond what you install yourself.",
        "Ask what must be removed, what must be reinstated and whether the landlord can require particular works. Get the starting condition and agreed exclusions recorded, supported by dated photographs. If removal is left to the landlord's election, check when that decision must be made.",
        "The exit cost belongs in the budget from day one."
      ]
    },
    {
      "heading": "Repairs can be a much bigger promise than maintenance",
      "paragraphs": [
        "Cleaning an air-conditioning filter and replacing an entire system are very different expenses. So are repainting a wall and repairing a structural defect.",
        "Check responsibility for existing defects, major equipment, building services and structural work. Do not assume everything expensive belongs to the landlord—or that every broad repair clause is enforceable exactly as written. The lease, its condition evidence and applicable law need to be considered together.",
        "Also look for refurbishment or redecoration obligations during the term. Could you face another round of spending before you have recovered the first fit-out cost?"
      ]
    },
    {
      "heading": "Can the landlord move you or end the lease early?",
      "paragraphs": [
        "Relocation and demolition clauses deserve attention before you invest in a location-specific fit-out.",
        "For covered retail leases, Queensland legislation provides protections and procedures that cannot simply be signed away. But that does not mean relocation or demolition can never occur, or that every business loss will be compensated.",
        "Check the contractual triggers alongside the statutory rules: notice, alternative premises, termination rights and compensation where applicable. Also compare the lease with any disclosure about redevelopment.",
        "The question is not just how long the lease appears to run. It is what could interrupt that term."
      ]
    },
    {
      "heading": "Keep an eye on personal guarantees and incentive repayment",
      "paragraphs": [
        "Taking a lease through a company does not protect your personal assets if you also give a personal guarantee or indemnity. Check what it covers, how long it lasts and what happens on renewal, assignment or termination.",
        "Look for incentive clawbacks too. A rent-free period or fit-out contribution may come with a repayment clause if particular events occur. Its enforceability and effect need checking; do not assume every demand would be valid.",
        "If you later sell the business, do not assume transferring the lease automatically releases you or your guarantor. There are statutory processes and protections for retail assignments, but the transaction and documents still matter."
      ]
    },
    {
      "heading": "Form 12 is part of the process, not the whole answer",
      "paragraphs": [
        "First check whether the Retail Shop Leases Act applies. Not every commercial tenancy falls within it.",
        "Where required, Form 12 is the legal advice report completed by an Australian lawyer and provided to the lessor before the prospective tenant or assignee enters the lease or assignment. It is not the landlord's disclosure statement, fit-out consent or financial advice report. Applicable exceptions also need checking.",
        "The useful work is understanding the deal behind the form: the lease, disclosure documents, plans, incentives and any earlier agreement.",
        "Before committing to the fit-out, ask:",
        "Those questions are easier to deal with before the shop is fitted out and the money is spent. Send the proposed documents and tell us what you have already signed or paid. We can help identify the legal issues while there is still an opportunity to address them."
      ],
      "bullets": [
        "Does the final paperwork match the agreed deal?",
        "What must I pay during the fit-out period and afterwards?",
        "Are the premises, permitted use and necessary approvals suitable?",
        "How secure are the term and renewal options?",
        "What must I repair, replace or remove?",
        "What could I still owe if the business closes or I sell it?"
      ]
    }
  ],
  "links": [
    {
      "to": "/retail-shop-leases",
      "label": "Retail shop lease review and Form 12 advice"
    },
    {
      "to": "/blog/$slug",
      "params": {
        "slug": "caloundra-shop-lease-form-12-before-signing"
      },
      "label": "Caloundra retail leases: what Form 12 does"
    },
    {
      "to": "/business-lawyers/$suburb",
      "params": {
        "suburb": "caloundra"
      },
      "label": "Business and commercial advice in Caloundra"
    }
  ],
  "cta": "Discuss the lease before committing to the fit-out",
  "disclaimer": "General information about Queensland leases, not legal, financial, building or engineering advice for a particular tenancy. Statutory coverage, exceptions and the actual documents require individual checking.",
  "sources": [
    {
      "label": "Retail Shop Leases Act 1994 (Qld)",
      "url": "https://www.legislation.qld.gov.au/view/whole/html/current/act-1994-047"
    },
    {
      "label": "Queensland Small Business Commissioner — make-good",
      "url": "https://www.qsbc.qld.gov.au/small-business-help/factsheets/make-good"
    },
    {
      "label": "Queensland Small Business Commissioner — outgoings",
      "url": "https://www.qsbc.qld.gov.au/small-business-help/factsheets/outgoings"
    },
    {
      "label": "Queensland Government — approved retail lease forms",
      "url": "https://www.publications.qld.gov.au/dataset/lease-forms-under-the-retail-shop-leases-act"
    }
  ]
},
{
  "slug": "buying-caloundra-business-lease-before-settlement",
  "title": "Buying a Caloundra business? Sort out the lease before settlement",
  "metaTitle": "Buying a Caloundra Business: Lease & Settlement",
  "description": "Buying a business in Caloundra? Michael Klein explains how the premises lease, landlord consent, disclosure and guarantees need to line up before settlement.",
  "date": "2026-11-04",
  "category": "Business & leases",
  "area": "Caloundra and Sunshine Coast",
  "author": "Michael Klein",
  "intro": "The equipment is there, the customers know the address and the seller is ready to hand over the keys. But buying the business does not, by itself, give you the right to occupy its premises. Sort out the lease as part of the purchase—not as an afterthought.",
  "sections": [
    {
      "heading": "First ask: assignment or a new lease?",
      "paragraphs": [
        "An assignment transfers the existing tenant’s lease interest to the buyer, subject to the documents and any required consent. A new lease is a separate arrangement with the landlord. Neither should be assumed just because the business is advertised for sale.",
        "With an assignment, you may be taking over the remaining term, rent reviews, options and obligations of an existing document. A new lease may offer different terms, but those terms still need to be negotiated. Identify which outcome the sale contract expects and what happens if it cannot be achieved."
      ]
    },
    {
      "heading": "Read the lease while you are considering the price",
      "paragraphs": [
        "A profitable-looking business may have only a short time left at its premises. Compare the remaining term and any renewal options with your plans and investment. An option needs to exist, remain available and be exercised in the required way; it is not the same as an unconditional promise of extra years.",
        "Read the permitted use, outgoings, repair and make-good clauses as well. If you want to change the operation, add services or alter the fit-out, check whether the lease and relevant approvals allow that. Existing trading is not proof that every proposed use is permitted.",
        "Ask who owns the installed equipment and fit-out, whether any items are financed, and what the tenant must remove at the end. The sale contract’s asset list and the lease obligations need to tell a consistent story."
      ]
    },
    {
      "heading": "Landlord consent is a process, not a handshake",
      "paragraphs": [
        "Check whether consent is required and what information must be provided about the incoming tenant. Queensland’s current Property Law Act provides a statutory consent process for certain lease dealings. Its application and interaction with the lease and retail legislation need checking; do not treat an informal conversation as the completed process.",
        "Ask early who will obtain consent, what supporting information is needed and who prepares the assignment or new lease. Keep the proposal, replies and agreed conditions in writing. A landlord asking for financial or business information can affect the timetable, so leave room for that work before settlement."
      ]
    },
    {
      "heading": "Retail assignments bring their own disclosure questions",
      "paragraphs": [
        "Not every business tenancy is a retail shop lease under Queensland’s Retail Shop Leases Act. The premises, business and statutory exclusions matter. If the Act applies, assignment disclosures and advice requirements need to be considered before the relevant commitments are made.",
        "The outgoing tenant and landlord can have separate disclosure obligations. Ask which documents must be given, to whom and when—including whether a requirement arises before the business sale contract, rather than only before the lease assignment. Do not assume a document handed over at settlement cures an earlier omission.",
        "A legal advice report is not financial advice about whether the business is worth buying. Have the accountant’s work, legal review and required reports coordinated without treating one as a substitute for another."
      ]
    },
    {
      "heading": "Who is personally liable after the handover?",
      "paragraphs": [
        "A company taking the lease may still be required to provide a personal guarantee. Check its scope, duration and what happens on renewal, assignment or default. Also identify any replacement bank guarantee, bond or other security and when it must be available.",
        "For the seller, handing over the business does not necessarily end lease liability. The retail legislation can provide a release for an outgoing tenant and guarantors where its conditions are met; other statutory rules and the transaction documents may also matter. Get the actual position checked rather than assuming either automatic release or permanent liability.",
        "Make the intended release and replacement security explicit in the documents. The buyer’s security is not simply the seller’s deposit with a new name on it."
      ]
    },
    {
      "heading": "Line up the sale contract with the lease outcome",
      "paragraphs": [
        "Before signing, discuss whether the sale should be conditional on suitable landlord consent, an effective assignment or an agreed new lease. Specify what must be achieved, by when, and the consequences if it is not. A condition only helps if its wording and operation fit the transaction.",
        "Consider this hypothetical example: a buyer agrees to purchase a Caloundra business, expecting to keep operating from the same premises. The landlord is willing to discuss a new lease, but no terms have been agreed. If the sale contract requires settlement regardless, the buyer may face an avoidable mismatch between buying the business and securing its location. The solution is to address that dependency before committing—not assume a later extension will be granted.",
        "Before completion, confirm that the necessary documents, consents, disclosures and security are ready and that the lease arrangement and business handover can take effect together. Early access, deposits and preliminary agreements deserve review too; leaving the final documents unsigned does not always mean nothing is binding."
      ]
    },
    {
      "heading": "Bring both sets of documents",
      "paragraphs": [
        "Send the proposed sale contract and the entire lease together, including variations, option notices, disclosure documents and any landlord correspondence. Tell us the proposed settlement date, what has already been signed or paid and what you intend to change about the business.",
        "That lets the legal review focus on the connection between the purchase and the premises. The best time to discover that the lease does not fit your plans is while there is still an opportunity to address it."
      ]
    }
  ],
  "links": [
    {
      "to": "/business-sales",
      "label": "Buying and selling a business"
    },
    {
      "to": "/business-lawyers/$suburb",
      "params": {
        "suburb": "caloundra"
      },
      "label": "Caloundra business and commercial lawyers"
    },
    {
      "to": "/guides/$slug",
      "params": {
        "slug": "buying-or-selling-a-business-in-queensland"
      },
      "label": "Queensland business purchase guide"
    },
    {
      "to": "/guides/$slug",
      "params": {
        "slug": "assigning-a-retail-shop-lease-queensland"
      },
      "label": "Retail lease assignment explained"
    }
  ],
  "cta": "Discuss the business purchase and premises lease together",
  "disclaimer": "General information about Queensland business purchases and leases, not advice on a particular transaction. The hypothetical example is not a client matter. Statutory coverage, transitional rules and the documents need individual review.",
  "sources": [
    {
      "label": "Property Law Act 2023 (Qld)",
      "url": "https://www.legislation.qld.gov.au/view/html/inforce/current/act-2023-027"
    },
    {
      "label": "Retail Shop Leases Act 1994 (Qld)",
      "url": "https://www.legislation.qld.gov.au/view/html/inforce/current/act-1994-047"
    },
    {
      "label": "Queensland Government — completing a business purchase",
      "url": "https://www.business.qld.gov.au/starting-business/starting-buying/buying/completing-purchase"
    },
    {
      "label": "Queensland Government — approved retail lease forms",
      "url": "https://www.publications.qld.gov.au/dataset/lease-forms-under-the-retail-shop-leases-act"
    }
  ]
},
  {
    slug: "pelican-waters-canal-home-title-pontoon-checks",
    title: "Buying a canal-front home in Pelican Waters? Look beyond the view",
    metaTitle: "Pelican Waters Canal Homes: Title & Pontoon Checks",
    description: "Michael Klein explains the title, pontoon, revetment wall and approval questions to ask before buying a canal-front home in Pelican Waters.",
    date: "2026-11-04",
    category: "Conveyancing tips",
    area: "Pelican Waters",
    author: "Michael Klein",
    image: { src: conveyancingImage, alt: "Property conveyancing illustration" },
    intro: "The view is lovely. The boat is practically calling your name. Before you commit to a canal-front home, though, it is worth checking what comes with the block—and what does not.",
    sections: [
      { heading: "Start with the title, not the sales photograph", paragraphs: ["A waterfront position does not, by itself, tell you where the lot ends or what rights you have over the water. The title and registered survey plan are a starting point for checking boundaries and registered interests, including easements and covenants.", "If boat access or a particular structure is important to the purchase, say so before signing. A title search is not an inspection, and a line on a plan does not prove that every waterfront improvement is approved."] },
      { heading: "The pontoon deserves its own questions", paragraphs: ["Ask for the available approvals and plans for any pontoon, jetty, ramp or waterfront deck. Check whether their position and use match the documents, and whether any conditions affect maintenance or proposed changes.", "Sunshine Coast Council identifies prescribed tidal works as a separate approval question. The applicable process depends on the location and work; neither an existing structure nor a registered title interest is automatic proof that the necessary approval exists."] },
      { heading: "Who looks after the wall?", paragraphs: ["Revetment walls and canal maintenance are not the same thing. Council's canal guidance distinguishes owner responsibilities for revetment walls from council's canal responsibilities. Have the obligations for the actual property checked, rather than assuming everything beyond the lawn is council's problem.", "Legal searches also do not tell you whether a wall or pontoon is structurally sound. If its condition matters, obtain advice from a suitably qualified specialist and ask what repair or replacement could involve."] },
      { heading: "Tell us what you want to do with the property", paragraphs: ["Perhaps you want to berth a different boat, replace a pontoon or extend a deck. Those plans should be discussed before you rely on the waterfront setup. Confirm the relevant authority requirements and any restrictions; do not assume the seller's current use settles the question."], bullets: ["Send the proposed contract, property address and any approval records you have.", "Identify the waterfront structures and intended boat access or alterations.", "Ask about title interests, maintenance obligations and specialist inspections.", "Tell us whether the contract is already signed and what deadlines apply."] },
      { heading: "A better time to ask is before you sign", paragraphs: ["These checks are about understanding the purchase, not talking you out of a canal home. Raise the questions early so the contract and enquiries can be considered together. Once you have signed, your options depend on the actual contract and its deadlines."] },
    ],
    links: [
      { to: "/conveyancing/$suburb", params: { suburb: "pelican-waters" }, label: "Pelican Waters contract review and conveyancing" },
      { to: "/conveyancing-sunshine-coast", label: "Sunshine Coast conveyancing and written quotes" },
    ],
    cta: "Tell us about the property and whether you have signed a contract.",
    disclaimer: "General information about Queensland property purchases, not legal or engineering advice about a particular lot or structure.",
    sources: [
      { label: "Sunshine Coast Council — canals and maintenance responsibilities", url: "https://www.sunshinecoast.qld.gov.au/environment/rivers-and-coast/canals" },
      { label: "Sunshine Coast Council — tidal works", url: "https://www.sunshinecoast.qld.gov.au/development/development-applications/tidal-works" },
      { label: "Titles Queensland — title searches", url: "https://www.titlesqld.com.au/title-searches/" },
    ],
  },
  {
    slug: "caloundra-shop-lease-form-12-before-signing",
    title: "Taking a shop lease in Caloundra? The rent is only the beginning",
    metaTitle: "Caloundra Shop Leases: Form 12 & Pre-signing Checks",
    description: "Michael Klein explains Form 12 legal advice reports and the lease clauses to check before committing to a Caloundra retail shop.",
    date: "2026-11-04",
    category: "Local community",
    area: "Caloundra",
    author: "Michael Klein",
    intro: "You have found the shop, worked out the rent and started thinking about the fit-out. Before the keys change hands, take a closer look at the lease. The expensive surprises are not always on the first page.",
    sections: [
      { heading: "Does the Retail Shop Leases Act apply?", paragraphs: ["Not every commercial tenancy is a retail shop lease under Queensland's Act. The premises, business and statutory exclusions matter. Check that first, because it affects disclosure and advice requirements. A shopfront address in Caloundra does not answer the question on its own."] },
      { heading: "What Form 12 actually does", paragraphs: ["Form 12 is the legal advice report under the Retail Shop Leases Act. Where required, an Australian lawyer completes it after advising the prospective tenant or assignee, and it is provided to the lessor before entering the lease or assignment.", "It is not the lease, the landlord's disclosure statement or a financial advice report. It does not certify that the business will succeed or make an unfavourable commercial deal a good one. Check the current requirements and any applicable exceptions for your transaction."] },
      { heading: "What could cost you more than the rent?", paragraphs: ["Read the lease and disclosure documents together. A headline rent is not the full occupancy cost, and a verbal assurance is not a substitute for checking what the documents actually require."], bullets: ["Outgoings: which costs can be passed on, how they are calculated and what is excluded?", "Refurbishment and redecoration: can the lease require a refit during the term?", "Demolition or relocation: what rights and protections apply?", "Turnover rent: is part of the rent linked to sales, and how is turnover defined?", "Fit-out and make-good: what must you install, maintain or remove?", "Guarantees: who is personally liable, and when does that liability end?"] },
      { heading: "An option is useful only if you exercise it properly", paragraphs: ["Check the dates, notice method and conditions for any renewal option. Missing the window can matter even when the landlord knows you want to stay. Put the actual dates in your diary and keep evidence of notices; do not rely on an informal conversation.", "Also check permitted use, assignment and any special conditions. If you later sell the business, the lease may be central to what you can transfer and on what terms."] },
      { heading: "Bring the whole deal, not just the signature page", paragraphs: ["Send the proposed lease, disclosure documents, special conditions and any agreement about fit-out or incentives. Tell us whether this is a new lease, renewal or assignment and whether you have already signed anything.", "We can discuss the legal review and Form 12 requirements separately from financial advice about the business. Where an accountant's financial advice report is required, the legal report does not replace it."] },
    ],
    links: [
      { to: "/retail-shop-leases", label: "Retail shop lease review and Form 12 advice" },
      { to: "/guides/$slug", params: { slug: "assigning-a-retail-shop-lease-queensland" }, label: "Assigning a Queensland retail shop lease" },
    ],
    cta: "Tell us about the proposed lease and any signing deadline.",
    editorial: true,
    disclaimer: "General information about Queensland retail leases, not advice about a particular tenancy. Statutory application, exceptions and current forms need checking for the transaction.",
    sources: [
      { label: "Retail Shop Leases Act 1994 (Qld)", url: "https://www.legislation.qld.gov.au/view/whole/html/current/act-1994-047" },
      { label: "Queensland Small Business Commissioner — retail shop lease forms", url: "https://qsbc.qld.gov.au/retail-shop-lease-forms/" },
      { label: "Queensland Government — Form 12 legal advice report", url: "https://www.publications.qld.gov.au/dataset/lease-forms-under-the-retail-shop-leases-act/resource/09efb024-38ff-4c43-b84e-45f73854dffb" },
    ],
  },
{
  "slug": "melbourne-cup-bets-office-sweeps-queensland",
  "title": "Your horse won. Your mate won’t pay. Now what?",
  "metaTitle": "Melbourne Cup Bets & Office Sweeps: Queensland Law",
  "description": "Is the office sweep legal? Can you enforce a bet against a mate? Michael Klein looks at Queensland betting law and three memorable court cases.",
  "date": "2026-11-03",
  "category": "Legal curiosities",
  "area": "Queensland",
  "author": "Michael Klein",
  "editorial": true,
  "intro": "Have you ever wondered whether the office sweep is legal? Or whether you can enforce a bet against your mate who’s welched one time too many? Probably not. It’s just the sort of thing lawyers think about while everyone else is picking a horse.",
  "sections": [
    {
      "heading": "A bet’s a bet. Or is it?",
      "paragraphs": [
        "It’s Melbourne Cup Day. Someone in the office has the sweep organised, someone else has studied the form, and someone has picked a horse because they like its name.",
        "Then comes the familiar offer: “Twenty dollars says mine beats yours.”",
        "Fair enough. But what happens if your horse wins and your mate won’t pay? Is a bet actually a contract you can enforce?",
        "You might expect a simple yes or no. Unfortunately, you’ve asked a lawyer.\n"
      ]
    },
    {
      "heading": "There’s a catch with the bet between mates",
      "paragraphs": [
        "In Queensland, being allowed to do something and being able to make someone pay for it are two different questions.",
        "Section 255 of the Racing Integrity Act 2016 starts by making contracts relating to betting void, subject to some important exceptions. It also catches certain related promises to pay and stops court claims for money or property won or lost on a bet, held by a stakeholder for the event, or lent or advanced for betting. [1]",
        "In other words, “but you promised” may not get you very far. Neither will writing the bet on a napkin. The problem isn’t necessarily proving what was said; it’s whether the law will enforce it at all.",
        "That provision doesn’t itself make placing the bet a criminal offence. Equally, it isn’t a permission slip for every kind of gambling. The arrangement still has to be looked at under the relevant rules.",
        "And before anyone decides this means bookmakers never have to pay, there are exceptions. We’ll come back to those."
      ]
    },
    {
      "heading": "This argument has been going on for a while",
      "paragraphs": [
        "Back in 1949, Tom Hill took his dispute with William Hill all the way to the House of Lords. He had lost money betting, and the bookmaker tried to recover it through a later repayment agreement, in return for holding off further action through the racing authorities.",
        "Hill won. The English law at the time barred court actions to recover money won on a wager. A new agreement didn’t get the bookmaker around that bar, even though it had promised something in return. The case is Hill v William Hill (Park Lane) Ltd [1949] AC 530. [2]",
        "Not quite the way most of us would choose to spend an afternoon at the races.",
        "That’s a historical English decision, not a shortcut out of paying every betting debt today. The law has since changed in England, Wales and Scotland: section 335 of the Gambling Act 2005, in force since September 2007, says a contract’s connection with gambling doesn’t itself stop it being enforced. Other reasons for refusing enforcement can still apply. [3]",
        "Then there’s the rather unlikely connection between betting and a Victorian flu remedy.",
        "The Carbolic Smoke Ball Company offered £100 to anyone who caught influenza after using its product as directed. Mrs Carlill did just that and asked for her money. The company argued, among other things, that the arrangement was a wager.",
        "The judges weren’t persuaded. In Carlill v Carbolic Smoke Ball Co [1892] 2 QB 484, affirmed [1893] 1 QB 256, the reward promise was upheld and the wagering argument rejected. [4]",
        "Hawkins J described a wager as involving each side’s chance of winning or losing, with neither having another interest in the bargain beyond the stake. That’s a historical description, not the complete test under every modern gambling law. But the point here is straightforward: a promise doesn’t become a bet just because payment depends on something uncertain.",
        "Mrs Carlill got her £100. Calling the promise a wager hadn’t made it disappear."
      ]
    },
    {
      "heading": "And if you lose rather more than twenty dollars?",
      "paragraphs": [
        "In Kakavas v Crown Melbourne Ltd [2013] HCA 25, the amount at stake was $20.5 million in net baccarat losses. The gambler argued that Crown had engaged in unconscionable dealing. [5]",
        "The High Court unanimously dismissed his appeal. The necessary exploitation of a relevant special disadvantage hadn’t been established.",
        "This isn’t a case to joke about: gambling problems can cause real harm. Nor does the decision mean a person with a gambling problem can never obtain legal relief. It means the size of a loss, on its own, doesn’t establish a right to get the money back. This was a Victorian casino case about unconscionable dealing, not the Queensland rule about a private wager."
      ]
    },
    {
      "heading": "So where does that leave today’s bets and the office sweep?",
      "paragraphs": [
        "For Queensland racing bookmakers, section 139 provides that a bet made at a licensed venue as part of lawful bookmaking is a valid contract on which either side can sue. Section 256 also removes the section 255 barrier for betting conducted under specified legislation, including the Wagering Act 1998 and the Charitable and Non-Profit Gaming Act 1999. [1]",
        "The Wagering Act separately makes specified betting under its framework lawful. So a properly regulated wager is not the same thing as your mate’s promise over lunch. Betting rules, mistakes, evidence and ordinary contract issues can still matter. [7]",
        "There’s also an exception for specified contributions towards prizes for lawful games and sports. It’s not a general exemption for any betting pool. [1]",
        "As for an individual’s Queensland office sweep, the category 1 rules allow gross proceeds of up to $2,000, provided the organiser takes no personal gain and all proceeds—or goods of equivalent value—go back to the players as prizes. You don’t have to give anything to charity to use that individual-sweep exception. [6]"
      ]
    },
    {
      "heading": "What about the office sweep?",
      "paragraphs": [
        "You do still have to comply with the applicable gaming rules and prize restrictions. If a prize includes alcohol or a gaming product, entries or ticket payments must not be accepted from minors. That’s not a blanket adults-only rule for every cash-prize sweep, although other restrictions may apply. [6]",
        "My practical suggestion is to sort out the prize split and what happens to scratched horses before anyone hands over money. Keep a record of who paid and which horse they drew.",
        "Pooling money to place bets needs a separate look. Section 11A of the Wagering Act restricts asking to act as someone else’s totalisator-betting agent, and acting as their agent for agreed consideration. Writing down contributions and sharing arrangements helps with evidence; it doesn’t make an otherwise unlawful or void arrangement valid. [7]"
      ]
    },
    {
      "heading": "Pooling bets and betting online",
      "paragraphs": [
        "If you’re betting online, check who you’re actually contracting with, where they’re licensed, the account terms and the complaints process. Your Queensland address doesn’t settle every question about which law applies, and a governing-law clause isn’t necessarily the last word either.",
        "For any disputed bookmaker bet, keep the confirmation, odds, account history and terms, and start with the operator’s complaints process. The appropriate regulator or dispute process may be the next step.",
        "Otherwise, enjoy the afternoon and don’t bet more than you can afford to lose. Just remember that winning the argument about whose horse is better and having an enforceable claim are not quite the same thing."
      ]
    },
    {
      "heading": "Enjoy the race",
      "paragraphs": [
        "Happy Cup Day from Coastside Law."
      ]
    }
  ],
  "image": {
    "src": "https://coastsidelaw.com.au/melbourne-cup-racing-2026.jpg",
    "alt": "Illustrative generated racing image of three horses and jockeys on a green racecourse; not a photograph of the Melbourne Cup"
  },
  "links": [
    {
      "to": "/our-people",
      "label": "Meet Michael and the Coastside Law team"
    },
    {
      "to": "/blog",
      "label": "More from Coastside Law"
    }
  ],
  "cta": "Contact Coastside Law",
  "disclaimer": "General information, not legal advice or a complete guide to running a sweep. The arrangement, jurisdiction and applicable law matter. For gambling support, contact Gambling Help Online on 1800 858 858.",
  "sources": [
    {
      "label": "[1] Racing Integrity Act 2016 (Qld), ss 139, 255–256",
      "url": "https://www.legislation.qld.gov.au/view/pdf/inforce/2026-11-03/act-2016-012"
    },
    {
      "label": "[2] Hill v William Hill (Park Lane) Ltd [1949] AC 530",
      "url": "https://vlex.co.uk/vid/hill-v-william-hill-792961189"
    },
    {
      "label": "[3] Gambling Act 2005 (UK), s 335",
      "url": "https://www.legislation.gov.uk/ukpga/2005/19/section/335"
    },
    {
      "label": "[4] Carlill v Carbolic Smoke Ball Co [1892] 2 QB 484 (trial judgment)",
      "url": "https://www.ato.gov.au/law/view/print?DocID=JUD%2F%2A1892%2A2QBD484%2F00001&PiT=99991231235958"
    },
    {
      "label": "[4] Carlill v Carbolic Smoke Ball Co [1893] 1 QB 256 (appeal)",
      "url": "https://law.justia.com/cases/foreign/united-kingdom/1-q-b-256-1893.html"
    },
    {
      "label": "[5] Kakavas v Crown Melbourne Ltd [2013] HCA 25 — official High Court summary",
      "url": "https://www.hcourt.gov.au/assets/publications/judgment-summaries/2013/hca25-2013-06-05.pdf"
    },
    {
      "label": "[6] Charitable and Non-Profit Gaming Act 1999 (Qld)",
      "url": "https://www.legislation.qld.gov.au/view/pdf/inforce/2026-11-03/act-1999-026"
    },
    {
      "label": "[6] Charitable and Non-Profit Gaming Regulation 1999 (Qld), reg 2B",
      "url": "https://www.legislation.qld.gov.au/view/pdf/inforce/2026-11-03/sl-1999-0283"
    },
    {
      "label": "[7] Wagering Act 1998 (Qld), ss 11, 11A, 12",
      "url": "https://www.legislation.qld.gov.au/view/pdf/inforce/2026-11-03/act-1998-015"
    },
    {
      "label": "[2] Star City Pty Ltd v Tan Hong Woon [2001] SGHC 100 — later discussion of Hill",
      "url": "https://www.elitigation.sg/gd/s/2001_SGHC_100"
    }
  ]
},
  {
    slug: "buying-together-golden-beach-pelican-waters",
    title: "Buying together in Golden Beach or Pelican Waters? Have this conversation first",
    metaTitle: "Buying Together in Golden Beach & Pelican Waters",
    description: "Buying a home together in Golden Beach or Pelican Waters? Discuss unequal deposits, ownership, mortgage responsibilities and an exit plan before signing.",
    date: "2026-10-22",
    category: "Conveyancing tips",
    area: "Golden Beach and Pelican Waters",
    image: { src: conveyancingImage, alt: "Coastal homes and a tree-lined street — illustrative property image" },
    intro: "You have found a home you both like. Before making an offer, pause for a less exciting but important conversation: whose money is going in, whose names will be on the title, and what happens if your plans change? These questions matter whether you are partners, relatives or friends buying together.",
    sections: [
      {
        heading: "An unequal deposit needs more than a handshake",
        paragraphs: [
          "One buyer might bring savings from a previous home while the other contributes more to repayments. Or a parent may help with the deposit. None of that should be left to assumptions about what is fair later. Write down the contributions and tell your solicitor and lender how the purchase is being funded.",
          "If family money is involved, clarify whether it is a gift, a loan or money intended to buy an ownership share. Those are different arrangements. A written agreement may be appropriate, and the people contributing money may need independent advice. An agreement also needs to work with the lender's requirements; it is not a way to keep an arrangement hidden from the bank.",
        ],
      },
      {
        heading: "The title and the mortgage answer different questions",
        paragraphs: [
          "The title records legal ownership. The loan documents set out obligations to the lender. A private arrangement to split repayments does not, by itself, change what the bank can require under those documents. Ask your lender to explain each borrower's liability rather than assuming you are responsible only for your agreed share.",
          "Joint tenancy and tenancy in common also have different consequences, particularly on death. Broadly, a joint tenant's interest passes to the surviving joint tenant or tenants, while a tenant in common's share forms part of their estate. We explain the comparison in our existing joint-ownership guide. The decision here is which structure fits your contributions and family plans, not which label sounds simpler.",
        ],
      },
      {
        heading: "Agree on the awkward situations while things are going well",
        paragraphs: ["A shared purchase should have a plan for change, not just settlement day. Discuss these questions before committing:"],
        bullets: [
          "Who pays rates, insurance, maintenance and unexpected repairs?",
          "What happens if one buyer cannot keep up with repayments?",
          "If one person wants to sell and the other does not, how will you decide what happens?",
          "How would a buyout be valued and funded?",
          "What should happen if a buyer dies or loses decision-making capacity?",
        ],
      },
      {
        heading: "Do not assume you can cheaply change it later",
        paragraphs: [
          "Adding an owner or changing shares after settlement can involve lender consent, transfer documents, tax considerations and transfer duty. Whether duty or a concession applies depends on the transaction and eligibility. Get advice before treating a later transfer as an easy fix.",
          "Buying a home is also a good time to review your will and enduring power of attorney. Your estate plans need to reflect how the property is actually held. A will should not be drafted on the assumption that every jointly owned asset becomes part of the estate.",
        ],
      },
      {
        heading: "What to bring to a pre-signing discussion",
        paragraphs: [
          "Bring the proposed contract, the buyers' full names, a summary of the deposit and repayment contributions, and details of any family funding. Mention an existing will, children from earlier relationships or a planned buyout arrangement if relevant. Each person should have the opportunity to get independent advice where their interests differ.",
          "Coastside Law can discuss the conveyancing and estate-planning questions together, so the ownership choice is considered before documents are signed. Share personal or financial documents only through a channel agreed with your solicitor, not through a public comment or social-media thread.",
        ],
      },
    ],
    links: [
      { to: "/guides/$slug", params: { slug: "joint-tenants-vs-tenants-in-common-queensland" }, label: "Joint tenants and tenants in common: the Queensland comparison" },
      { to: "/golden-beach", label: "Talk to Coastside Law in Golden Beach" },
      { to: "/wills-and-estates", label: "Wills and estate planning" },
      { to: "/stamp-duty-calculator-qld", label: "Estimate Queensland transfer duty" },
    ],
    sources: [
      { label: "Titles Queensland: survivorship and recording the death of a joint tenant", url: "https://www.titlesqld.com.au/manual-guides/guides-to-common-registry-transactions/dealing-with-a-death/recording-the-death-of-a-joint-tenant/" },
      { label: "Queensland Revenue Office: home and change-of-tenure duty exemptions", url: "https://qro.qld.gov.au/duties/transfer-duty/exemptions/home-property-owner/" },
      { label: "Queensland Revenue Office: transfer duty", url: "https://qro.qld.gov.au/duties/transfer-duty/" },
    ],
    cta: "Discuss your shared purchase before signing",
  },
  {
    slug: "established-home-golden-beach-renovation-checks",
    title: "Buying an established Golden Beach home? Check the renovations, not just the rooms",
    metaTitle: "Golden Beach Home Purchases: Renovation Checks",
    description: "Buying an established Golden Beach home? Check extensions, decks, plumbing records and easements, and understand what seller disclosure does not tell you.",
    date: "2026-10-22",
    category: "Conveyancing tips",
    area: "Golden Beach and Caloundra",
    image: { src: conveyancingImage, alt: "Established coastal homes — illustrative property image" },
    intro: "An enclosed patio, a new bathroom or a converted garage can make an established house feel just right. The useful question is not only whether the work looks good. It is whether the records, approvals and intended use match what you are buying. Here is how to investigate without confusing a building inspection with a legal check.",
    sections: [
      {
        heading: "Start with what has changed",
        paragraphs: [
          "At the inspection, note additions and alterations: a deck, shed, carport, extra bathroom, enclosed space or pool. Ask the agent what work was done, when it was done and what records the seller holds. An answer such as 'it was like that when we bought it' is a reason to investigate, not proof of approval.",
          "Do not assume that every alteration needed the same approval, or that every older structure is unlawful. Requirements depend on the work and the rules applying at the time. Equally, age alone does not prove a structure is approved. A solicitor can identify the records to request; a building certifier or council may need to clarify the technical position.",
        ],
      },
      {
        heading: "A building inspection and a records search do different jobs",
        paragraphs: [
          "A building and pest inspection assesses matters within the inspector's scope, including condition and accessible defects. It is not automatically a council approvals audit. Tell the inspector about alterations that concern you and ask what their report will and will not cover.",
          "Sunshine Coast Council offers property searches covering building, plumbing and development information, as well as other property records. Search availability, fees and turnaround differ. Ask which searches suit this property and what gaps may remain, rather than ordering a generic package and assuming it answers everything.",
        ],
      },
      {
        heading: "Check the land beneath the improvements",
        paragraphs: [
          "A title search and registered plan can identify the lot and registered interests such as easements. Compare that information with how you expect to use the yard. A planned extension, pool or shed may need more investigation if an easement or other restriction affects its proposed location.",
          "The title does not tell you whether a bathroom was approved or a deck is structurally sound. Planning information, approvals records and physical inspections answer different questions. Your due diligence should connect those pieces rather than treating one document as a complete picture.",
        ],
      },
      {
        heading: "Form 2 is the starting point, not a condition report",
        paragraphs: [
          `Queensland's seller disclosure scheme began on ${SELLER_DISCLOSURE_START_LABEL}. It generally requires sellers to provide the disclosure statement and prescribed documents before the buyer signs, subject to exceptions. That does not mean every renovation, defect or property risk is covered.`,
          "The Queensland Government specifically identifies matters outside the scheme, including building and pest condition, structural soundness, flooding history and building or development approvals. Read the disclosure carefully, then investigate the questions it does not answer. Termination rights for disclosure problems have conditions; do not assume any missing detail lets you walk away.",
        ],
      },
      {
        heading: "Resolve important gaps before you commit",
        paragraphs: [
          "If the extra room is essential to your purchase, say so before signing. Your solicitor can advise on further enquiries and whether a tailored condition is appropriate. A standard building and pest condition should not be assumed to give a broad right to terminate for any approvals concern. An extension of time also depends on the contract and the other party's agreement.",
          "Send the contract and disclosure documents with a short list of the improvements you want checked. Include your plans for the property. That helps focus the review on the house you are actually buying, not just its address and purchase price.",
        ],
      },
    ],
    links: [
      { to: "/guides/$slug", params: { slug: "unapproved-building-work-decks-sheds-pools-queensland" }, label: "Unapproved building work: decks, sheds and pools" },
      { to: "/guides/$slug", params: { slug: "due-diligence-clause-queensland" }, label: "How a Queensland due-diligence condition works" },
      { to: "/form-2-buyer-rights-qld", label: "Form 2 and Queensland buyer rights" },
      { to: "/conveyancing/$suburb", params: { suburb: "caloundra" }, label: "Caloundra conveyancing and contract review" },
    ],
    sources: [
      { label: "Sunshine Coast Council: property searches", url: "https://www.sunshinecoast.qld.gov.au/development/searches/property-searches" },
      { label: "Queensland Government: seller disclosure scheme and its exclusions", url: "https://www.qld.gov.au/housing/buying-owning-home/seller-disclosure-scheme" },
    ],
    cta: "Ask about an established-home purchase",
  },
  {
    slug: "golden-beach-pumicestone-passage-coastal-change",
    title: "Golden Beach and the changing Pumicestone Passage: where residents can find reliable information",
    metaTitle: "Golden Beach Coastal Change: A Local Resident Guide",
    description: "Golden Beach coastal change explained: Bribie Island breakthrough updates, council storm-tide information and how residents can check an individual property.",
    date: "2026-10-22",
    category: "Local community",
    area: "Golden Beach and Pumicestone Passage",
    image: { src: goldenBeachImage, alt: "Golden Beach waterfront overlooking Pumicestone Passage — site cover image, not hazard mapping" },
    intro: "The Passage and foreshore are part of daily life at Golden Beach. They are also changing coastal environments. If you live here, or are considering a move, it helps to separate council information from speculation about what a change means for a particular home. This is a starting point for finding reliable updates, not a risk assessment of the suburb.",
    sections: [
      {
        heading: "Why the Bribie Island breakthrough matters locally",
        paragraphs: [
          "Sunshine Coast Council's Bribie Island breakthrough action plan focuses on protecting the Golden Beach and Diamond Head foreshores. Council's storm-tide mapping update explains that further study followed the 2022 breakthrough. Those are useful sources for understanding why the foreshore and coastal hazards are being discussed together.",
          "Responsibilities are shared. Council's action-plan page explains that the State manages Bribie Island and that Pumicestone Passage sits within Moreton Bay Marine Park. A question about a foreshore project is not necessarily directed to the same authority as a question about island management or marine-park activities.",
        ],
      },
      {
        heading: "Erosion, storm tide and flooding are not interchangeable",
        paragraphs: [
          "Coastal erosion concerns changes to the shoreline. Storm-tide inundation concerns coastal land being covered by elevated sea levels during storm conditions. Flood information can address other processes, such as catchment flooding. Do not assume one map answers every question about water at an address.",
          "Council's coastal hazard adaptation strategy considers erosion, storm-surge inundation and expanding tidal areas associated with sea-level rise. A strategy describes long-term planning; it is not a promise about future works at one home or a forecast of what will happen on a particular day.",
        ],
      },
      {
        heading: "Does Golden Beach flood? Start with the address",
        paragraphs: [
          "There is no responsible single yes-or-no answer for every property in Golden Beach. Check council information for the specific lot, what type of hazard is mapped, and the scenario or assumptions used. Then ask council or a suitably qualified adviser what the information means for your question.",
          "Council's storm-tide update makes an important distinction: more detailed research does not itself create a new physical risk. Updated information can improve understanding of an existing hazard. A map also should not be read as proof that a property has flooded in the past, or that an unmapped property has no exposure.",
        ],
      },
      {
        heading: "A useful checklist for residents and future residents",
        paragraphs: ["Use original sources and keep the question specific:"],
        bullets: [
          "Read the council breakthrough action plan for the project context and the agencies involved.",
          "Check the date and scope of the council storm-tide and property-search information you are using.",
          "For a purchase or renovation, check the individual lot and relevant planning information rather than relying on a suburb-wide claim.",
          "Ask an insurer about cover, exclusions and costs for the actual address; a council map is not an insurance offer.",
          "Use council's disaster information for current warnings and emergency preparation, rather than a property article or an old social-media post.",
        ],
      },
      {
        heading: "Keep community updates separate from purchase advice",
        paragraphs: [
          "A discussion about a public foreshore project is not evidence that a particular home will gain or lose value, become uninsurable or receive a protective structure. This article makes none of those claims. For current project status, works and consultation opportunities, follow the responsible authority's latest notices.",
          "If you are buying, tell your solicitor which coastal or flooding questions matter to you before signing. The review can identify appropriate searches and whether the contract needs a tailored condition. Queensland seller disclosure is not a substitute for investigating flooding history or the risks relevant to your intended use.",
          "For neighbours who simply want to understand local changes, the council resources below are the best next step. A legal enquiry is only needed when you have a property or contract question specific to your situation.",
        ],
      },
    ],
    links: [
      { to: "/golden-beach", label: "Coastside Law's Golden Beach office" },
      { to: "/guides/$slug", params: { slug: "buying-a-house-you-cannot-insure-flood-storm-surge-queensland" }, label: "Flood, storm surge and insurance: questions for property buyers" },
      { to: "/form-2-buyer-rights-qld", label: "What Queensland seller disclosure covers" },
    ],
    sources: [
      { label: "Sunshine Coast Council: Bribie Island breakthrough action plan", url: "https://www.sunshinecoast.qld.gov.au/environment/rivers-and-coast/breakthrough-action-plan" },
      { label: "Sunshine Coast Council: coastal hazard adaptation strategy", url: "https://www.sunshinecoast.qld.gov.au/council/planning-and-projects/council-strategies/coastal-hazard-adaptation-strategy" },
      { label: "Sunshine Coast Council: storm-tide mapping update (2024)", url: "https://www.sunshinecoast.qld.gov.au/news/council-updates-storm-tide-mapping-for-coast" },
      { label: "Sunshine Coast Council: property searches", url: "https://www.sunshinecoast.qld.gov.au/development/searches/property-searches" },
      { label: "Sunshine Coast Council: disaster information and warnings", url: "https://disaster.sunshinecoast.qld.gov.au/" },
      { label: "Queensland Government: seller disclosure scheme", url: "https://www.qld.gov.au/housing/buying-owning-home/seller-disclosure-scheme" },
    ],
    cta: "Ask a Golden Beach property question",
  },
  {
    slug: "buying-in-caloundra-checks-before-you-sign",
    title: "Buying in Caloundra or Golden Beach? Five checks before you sign",
    metaTitle: "Buying in Caloundra: 5 Checks Before You Sign",
    description:
      "Buying a home in Caloundra, Golden Beach or Pelican Waters? Five things to check before you sign a Queensland contract, from a local conveyancing solicitor.",
    date: "2026-10-03",
    category: "Conveyancing tips",
    area: "Caloundra and Golden Beach",
    intro:
      "Most problems in a Queensland purchase are locked in the moment you sign. Once the contract is signed, your options narrow quickly. Here are the five things we check for buyers before they put pen to paper.",
    sections: [
      {
        heading: "1. Read the seller's disclosure (Form 2)",
        paragraphs: [
          "Since 1 August 2025 most Queensland sellers must give you a disclosure statement before you sign. It covers the title, easements, council and body corporate details and some notices. Read it before the contract, not after.",
        ],
      },
      {
        heading: "2. Check the finance and building and pest dates",
        paragraphs: [
          "These dates decide how long you have to get your loan approved and your inspections done. If they're too tight, ask for more time before you sign. It's much harder to get an extension later.",
        ],
      },
      {
        heading: "3. Look at the special conditions",
        paragraphs: [
          "Special conditions change the standard contract. Some are harmless. Others shift risk onto you, such as a short settlement or a clause that lets the seller keep fittings you assumed were included.",
        ],
      },
      {
        heading: "4. Know what cooling-off actually gives you",
        paragraphs: [
          "Most residential buyers get five business days to cool off, but walking away costs 0.25% of the price. Cooling-off is a safety net, not a substitute for checking the contract first.",
        ],
      },
      {
        heading: "5. Units and townhouses: read the body corporate details",
        paragraphs: [
          "Check the levies, any special levies coming up, and whether there are disputes or major repairs on the horizon. Coastal buildings can need costly work on concrete, balconies and waterproofing.",
        ],
      },
      {
        heading: "Send us the contract first",
        paragraphs: [
          "When we act on your purchase, we review the contract before you sign at no extra cost. A purchase is a fixed $1,499 including GST.",
        ],
      },
    ],
    links: [
      { to: "/form-2-buyer-rights-qld", label: "Form 2 seller disclosure explained" },
      { to: "/cooling-off-period-qld", label: "The QLD cooling-off period" },
      { to: "/special-conditions-contract-qld", label: "Special conditions in QLD contracts" },
      { to: "/conveyancing/$suburb", params: { suburb: "caloundra" }, label: "Conveyancing in Caloundra" },
    ],
    cta: "Send us your contract to review",
  },
  {
    slug: "selling-on-the-redcliffe-peninsula-get-ready-before-listing",
    title: "Selling on the Redcliffe Peninsula? Get these ready before you list",
    metaTitle: "Selling in Redcliffe: Get Ready Before You List",
    description:
      "Selling in Redcliffe, Scarborough or Newport? What Queensland sellers need ready before listing, including the Form 2 disclosure statement. Fixed $990 sale fee.",
    date: "2026-10-03",
    category: "Conveyancing tips",
    area: "Redcliffe, Scarborough and Newport",
    intro:
      "If you're selling on the Redcliffe Peninsula, the legal work starts before the sign goes up. Queensland sellers now have to give buyers a disclosure statement before a contract is signed, so it pays to have it ready.",
    sections: [
      {
        heading: "The disclosure statement must come first",
        paragraphs: [
          "Most sellers must give buyers a Form 2 disclosure statement and the required documents before the buyer signs. If it's missing or wrong, the buyer may be able to end the contract. Having it prepared early means you don't lose a keen buyer while it's being sorted.",
        ],
      },
      {
        heading: "What we'll ask you for",
        paragraphs: ["Gathering these early speeds everything up:"],
        bullets: [
          "Your latest rates and water notices",
          "Body corporate details, if it's a unit or townhouse",
          "Any pool safety certificate, or confirmation there's no pool",
          "Details of any approvals for extensions, sheds or granny flats",
          "Your mortgage lender's details so we can arrange discharge",
        ],
      },
      {
        heading: "Mortgage discharge takes time",
        paragraphs: [
          "Banks can take a couple of weeks to process a discharge request. Lodging it as soon as you have a contract helps you settle on time.",
        ],
      },
      {
        heading: "A fixed fee from our Scarborough office",
        paragraphs: [
          "A sale is a fixed $990 including GST, agreed before we start. Our Scarborough office is at 15 Fortune Street.",
        ],
      },
    ],
    links: [
      { to: "/seller-disclosure", label: "Seller disclosure in Queensland" },
      { to: "/redcliffe-conveyancing", label: "Redcliffe conveyancing" },
      { to: "/conveyancing-cost-qld", label: "What conveyancing costs in QLD" },
    ],
    cta: "Get your seller disclosure started",
  },
  {
    slug: "what-happens-on-settlement-day-qld",
    title: "What actually happens on settlement day in Queensland",
    metaTitle: "What Happens on Settlement Day in QLD",
    description:
      "Settlement day in Queensland explained simply: how electronic settlement works, when you get the keys, and what buyers and sellers should do that week.",
    date: "2026-10-03",
    category: "Conveyancing tips",
    area: "Queensland",
    intro:
      "Settlement day is when the money moves and ownership changes. In Queensland it almost always happens online, and you don't need to be anywhere for it. Here's what goes on behind the scenes.",
    sections: [
      {
        heading: "It happens online",
        paragraphs: [
          "Your lawyer, the other side's lawyer and the banks all sign off in an electronic settlement workspace, usually PEXA. When everyone is ready, the funds move and the transfer is lodged for registration at the same time.",
        ],
      },
      {
        heading: "When you get the keys",
        paragraphs: [
          "Once we confirm settlement has gone through, we let you and the agent know. The agent then releases the keys. That's often early to mid afternoon, but it depends on the banks.",
        ],
      },
      {
        heading: "The week before: what to do",
        bullets: [
          "Buyers: book your pre-settlement inspection and confirm your funds are ready",
          "Sellers: move out, leave inclusions in place and hand keys to the agent",
          "Both: answer your lawyer and bank quickly — delays usually start with a missing signature",
        ],
        paragraphs: [],
      },
    ],
    links: [
      { to: "/pexa-settlement", label: "How PEXA settlement works" },
      { to: "/guides/$slug", params: { slug: "pre-settlement-inspection-checklist-queensland" }, label: "Pre-settlement inspection checklist" },
      { to: "/conveyancing-sunshine-coast", label: "Conveyancing on the Sunshine Coast" },
    ],
    cta: "Ask about your settlement",
  },
{
  "slug": "signs-your-will-needs-updating-qld",
  "title": "Five signs your will needs updating",
  "metaTitle": "Reviewing Your Will After Life Changes | Queensland",
  "description": "Moved house, changed your family plans or bought a business? Michael Klein explains when to review a Queensland will and how ownership affects the estate.",
  "date": "2026-10-03",
  "lastmod": "2026-11-04",
  "category": "Wills",
  "area": "Golden Beach, Caloundra and Pelican Waters",
  "author": "Michael Klein",
  "intro": "You have changed the house, the family has grown or the business looks very different from the day you signed your will. Does the document still fit? A review is about checking what you want now—not assuming that every life change automatically invalidates a will.",
  "sections": [
    {
      "heading": "1. You have married or changed your relationship",
      "paragraphs": [
        "Marriage can revoke an earlier will in Queensland, subject to statutory exceptions, including wills made in contemplation of the marriage. Do not assume an old document remains effective simply because your wishes have not changed. Tell your solicitor about a planned or recent marriage and have the actual wording checked.",
        "Separation and divorce are different legal events. Separation alone does not generally have the same effect as divorce on a will. Divorce can affect gifts, appointments and other provisions involving a former spouse, subject to the law and the document. Neither event should be treated as a substitute for reviewing the whole estate plan.",
        "If you are in a blended family, discuss what should happen for your partner and children, including children from earlier relationships. A simple promise that everyone will be looked after is not a plan for competing interests or later changes."
      ]
    },
    {
      "heading": "2. Children, grandchildren or beneficiaries’ needs have changed",
      "paragraphs": [
        "A new child or grandchild is a good reason to check who is included, whether the wording covers later family additions and how a young beneficiary would receive their gift. It is also a chance to consider a guardian nomination for minor children and ask what that nomination can and cannot achieve.",
        "Some beneficiaries need more support than a straightforward gift. Age, disability, financial difficulties or vulnerability can affect the arrangements worth considering. A trust may be relevant, but it is not automatically necessary or suitable for every family. Ask about the options and the ongoing responsibilities before choosing one.",
        "Review specific gifts too. An item may have been sold, an account closed or a beneficiary may have died. The result depends on the wording and applicable law; do not assume your executor can simply substitute something else."
      ]
    },
    {
      "heading": "3. You have bought, sold or changed ownership of property",
      "paragraphs": [
        "Moving from a Golden Beach house to a Caloundra unit does not, by itself, mean you need an entirely new will. But a change in assets, ownership or your intended gifts is worth checking. The important question is how the property is held and what the will actually says.",
        "Joint tenancy and tenancy in common have different consequences. A joint tenant’s interest generally passes by survivorship to the surviving joint owner, outside the will. A tenant-in-common share generally forms part of the estate. Being “joint owners” in everyday conversation does not settle which arrangement is on the title.",
        "Consider this hypothetical example: two people buy a Pelican Waters home and expect their respective children to inherit their shares. If the title is held as joint tenants, a will alone may not produce that result on the first death. Have the ownership and estate plan considered together before assuming they match.",
        "Superannuation, insurance, trust assets and company property also need separate attention. They do not all pass through your personal will in the same way. Check the relevant ownership, nominations and governing documents rather than putting every asset into one assumed estate list."
      ]
    },
    {
      "heading": "4. Your business or financial arrangements have changed",
      "paragraphs": [
        "Starting or buying a business can introduce company shares, partnerships, loans, guarantees and other commitments. Your will should be reviewed alongside those arrangements, not treated as a complete business-succession plan.",
        "The company’s assets are not simply your personal assets because you control the company. Similarly, a trust’s assets and control arrangements require their own review. Discuss the relevant agreements, debts and decision-making roles with your solicitor and accountant.",
        "It is useful to separate what happens on death from what happens if you cannot make decisions during life. A will operates after death. An enduring power of attorney serves a different purpose, and its scope and operation need advice. Neither document necessarily solves every business-continuity issue."
      ]
    },
    {
      "heading": "5. Your executor or attorney is no longer the right person",
      "paragraphs": [
        "An executor who was an obvious choice ten years ago may now be unwell, unwilling or difficult to contact. Moving away does not automatically disqualify someone, but availability, capability and potential conflicts are worth considering. Check whether your will names a suitable substitute.",
        "Have the conversation with the person you want to appoint. Being executor can involve locating documents, dealing with institutions, keeping records and administering the estate—not simply signing a probate form. Your choice should reflect that work.",
        "Review your attorney appointments separately. An attorney acts under an enduring power of attorney during your lifetime; an executor’s role relates to the estate after death. The same person may be suitable for both, but that is a decision to make deliberately."
      ]
    },
    {
      "heading": "Bring the plan, not just the old document",
      "paragraphs": [
        "Bring your existing will and enduring power of attorney, a broad asset and debt list, ownership information and a note of what has changed. Mention companies, trusts, superannuation arrangements and any concerns about beneficiaries or decision-making capacity. Agree a private way to provide sensitive documents; do not put family instructions in public comments.",
        "Do not write changes onto the original will or assume a handwritten note is an effective amendment. The legal formalities and your capacity at the time matter. We can discuss whether a new will or another approach is appropriate and quote the work before it starts.",
        "A review should leave you knowing what the documents do, what sits outside them and what needs attention elsewhere. That is more useful than updating the date and hoping the rest still works."
      ]
    }
  ],
  "links": [
    {
      "to": "/wills/$suburb",
      "params": {
        "suburb": "caloundra"
      },
      "label": "Wills and estate planning in Caloundra"
    },
    {
      "to": "/wills-estate-lawyers-sunshine-coast",
      "label": "Sunshine Coast wills and enduring powers of attorney"
    },
    {
      "to": "/guides/$slug",
      "params": {
        "slug": "joint-tenants-vs-tenants-in-common-queensland"
      },
      "label": "Joint tenancy and tenancy in common"
    },
    {
      "to": "/guides/$slug",
      "params": {
        "slug": "how-to-apply-for-probate-in-queensland"
      },
      "label": "The executor’s probate guide"
    }
  ],
  "cta": "Discuss what has changed and book a will review",
  "disclaimer": "General information about Queensland estate planning, not advice for your circumstances. The Pelican Waters example is hypothetical, not a client matter.",
  "sources": [
    {
      "label": "Succession Act 1981 (Qld)",
      "url": "https://www.legislation.qld.gov.au/view/html/inforce/current/act-1981-069"
    },
    {
      "label": "Queensland Government — enduring powers of attorney",
      "url": "https://www.qld.gov.au/law/legal-mediation-and-justice-of-the-peace/power-of-attorney-and-making-decisions-for-others/power-of-attorney"
    },
    {
      "label": "Titles Queensland — recording the death of a joint tenant",
      "url": "https://www.titlesqld.com.au/manual-guides/guides-to-common-registry-transactions/dealing-with-a-death/recording-the-death-of-a-joint-tenant/"
    }
  ]
},
  {
    slug: "first-steps-as-an-executor-queensland",
    title: "Named as executor? Your first steps in Queensland",
    metaTitle: "Named as Executor? First Steps in Queensland",
    description:
      "Just been named executor of a Queensland estate? The first steps to take, what not to do yet, and when you'll need probate. From a local estates solicitor.",
    date: "2026-10-03",
    category: "Probate",
    area: "Sunshine Coast and Moreton Bay",
    intro:
      "Being named executor usually comes at a hard time. The good news is that very little has to happen in the first few weeks. Here's where to start, and what to leave until later.",
    sections: [
      {
        heading: "Find the original will",
        paragraphs: [
          "The court needs the original, not a copy. Check the person's papers, and ask the law firm that made the will whether they hold it in safe custody.",
        ],
      },
      {
        heading: "Secure the assets",
        paragraphs: [
          "Make sure the house is locked and insured, and tell the banks about the death so accounts are frozen. Keep receipts for anything you pay.",
        ],
      },
      {
        heading: "Don't hand anything out yet",
        paragraphs: [
          "Family provision claims in Queensland can be started up to nine months after death. Paying beneficiaries too early can leave you personally exposed if a claim comes in.",
        ],
      },
      {
        heading: "Find out whether you need probate",
        paragraphs: [
          "Banks and the Titles Office often need probate before they'll release funds or transfer property. Before applying, notice of the application must be published, and the court then needs time to process it.",
        ],
      },
    ],
    links: [
      { to: "/probate-checklist-queensland", label: "Executor probate checklist" },
      { to: "/probate-queensland", label: "Probate in Queensland" },
      { to: "/probate-enquiry", label: "Probate enquiry" },
    ],
    cta: "Talk to us about the estate",
  },
  {
    slug: "conveyancing-costs-sunshine-coast-2026",
    title: "What conveyancing really costs on the Sunshine Coast in 2026",
    metaTitle: "Conveyancing Costs Sunshine Coast 2026 | Itemised",
    description:
      "An itemised look at Sunshine Coast conveyancing costs in 2026: the professional fee, searches, government fees and transfer duty, from a local solicitor.",
    date: "2026-10-12",
    category: "Conveyancing tips",
    area: "Sunshine Coast",
    intro:
      "When people compare conveyancing quotes on the Sunshine Coast, they usually compare the headline fee. That's only part of the cost. Here is what a purchase or sale actually involves, and where quotes tend to grow.",
    sections: [
      {
        heading: "The professional fee",
        paragraphs: [
          "This is what the firm charges for its work. Ours is fixed: $990 to sell and $1,499 to buy, both including GST. Check whether a quote says \"from\", and whether calls, emails, file set-up or PEXA handling are charged on top.",
        ],
      },
      {
        heading: "Searches and government fees",
        paragraphs: [
          "Buyers pay for title, council and other searches, plus registration fees. On a typical Sunshine Coast purchase these come to about $450 to $800. A unit adds a body corporate search, and acreage or canal property may need more enquiries.",
        ],
      },
      {
        heading: "Transfer duty",
        paragraphs: [
          "For buyers, transfer duty is usually the biggest cost by far. It depends on the price and whether a home or first home concession applies. It is paid to the Queensland Revenue Office, not to your conveyancer.",
        ],
      },
      {
        heading: "Where quotes grow",
        paragraphs: ["Watch for these when comparing quotes:"],
        bullets: [
          "Hourly charges once the matter becomes \"complex\"",
          "Separate fees for off-the-plan or body corporate work",
          "Fees for settlement problems, such as a delayed settlement or extension",
          "Being charged in full when the contract falls over during cooling-off",
        ],
      },
    ],
    links: [
      { to: "/conveyancing-sunshine-coast", label: "Sunshine Coast conveyancing" },
      { to: "/stamp-duty-calculator-qld", label: "Queensland stamp duty calculator" },
      { to: "/fees", label: "Our full fee schedule" },
    ],
    cta: "Want a fixed, itemised quote for your Sunshine Coast property? Send us the address and we'll reply the same business day.",
  },
  {
    slug: "buying-a-unit-kings-beach-body-corporate",
    title: "Buying a unit at Kings Beach: reading the body corporate records",
    metaTitle: "Buying a Unit Kings Beach | Body Corporate Checks",
    description:
      "Buying a unit at Kings Beach or elsewhere in Caloundra? What to look for in the body corporate records before cooling-off ends, from a local solicitor.",
    date: "2026-10-12",
    category: "Conveyancing tips",
    area: "Caloundra",
    intro:
      "When you buy a unit at Kings Beach, you're also buying into a building and the committee that runs it. The records tell you how well that building has been looked after and what it is likely to cost you. Here's what we look for.",
    sections: [
      {
        heading: "The sinking fund",
        paragraphs: [
          "The sinking fund pays for major works. Compare its balance with the forecast. An older beachfront building with a small fund is a warning sign that a special levy may be coming.",
        ],
      },
      {
        heading: "Minutes of recent meetings",
        paragraphs: [
          "Committee and general meeting minutes show what is being discussed: concrete cancer, balcony repairs, lift replacement, waterproofing or legal disputes. This is often where you'll find a problem that isn't mentioned anywhere else.",
        ],
      },
      {
        heading: "Insurance",
        paragraphs: [
          "Building insurance for coastal schemes has risen sharply. Check the premium, the excess and any claims history, because they feed directly into your levies.",
        ],
      },
      {
        heading: "By-laws and letting",
        paragraphs: [
          "If you plan to holiday-let or have a pet, the by-laws and any letting agreement decide whether you can. Read them before you rely on rental income.",
        ],
      },
    ],
    links: [
      { to: "/conveyancing/$suburb", label: "Caloundra conveyancing", params: { suburb: "caloundra" } },
      { to: "/guides/$slug", label: "The Queensland cooling-off period", params: { slug: "cooling-off-period-queensland" } },
    ],
    cta: "Buying a unit in Caloundra? Send us the contract and we'll check it and the body corporate records before cooling-off ends.",
  },
  {
    slug: "preparing-a-probate-application-queensland",
    title: "Getting a probate application right the first time",
    metaTitle: "Preparing a Probate Application in Queensland",
    description:
      "What to gather before applying for probate in Queensland, the hold-ups that delay a grant, and what happens after it issues. Michael Klein explains.",
    date: "2026-11-04",
    category: "Probate",
    area: "Sunshine Coast and Moreton Bay",
    author: "Michael Klein",
    intro:
      "Most executors I meet expect probate to be the hard part. Usually it isn't. A standard Queensland application is dealt with on the papers, and nobody goes to court. What slows things down is nearly always the same few problems, and most of them can be sorted out before anything is filed. Here's what I'd want you to know before you start.",
    sections: [
      {
        heading: "First, check you actually need it",
        paragraphs: [
          "Not every estate needs a grant. A house owned as joint tenants passes to the surviving owner automatically. Superannuation and life insurance often get paid outside the will. Some banks will release a small balance without probate.",
          "Where the person owned real estate in their own name, or held larger amounts with banks or share registries, you'll usually need one. Ask each institution what it requires before you spend money on an application.",
        ],
      },
      {
        heading: "What to gather before you call",
        paragraphs: [
          "If you bring these to the first meeting, we can usually get the notice out straight away:",
        ],
        bullets: [
          "The original signed will and any codicils, not a copy. Don't unstaple it, write on it or remove anything attached",
          "The death certificate",
          "A rough list of assets and debts: property, bank accounts, shares, vehicles, super and any loans",
          "Contact details for the other executors and the beneficiaries",
          "Anything that explains a name difference, such as a maiden name or a name the person was known by",
        ],
      },
      {
        heading: "The notice and the 14-day wait",
        paragraphs: [
          "Before filing, the executor has to publish a notice of intention to apply for a grant and serve a copy on the Public Trustee of Queensland. The application can't be filed until at least 14 days after that.",
          "That fortnight isn't wasted. It's when we finalise the executor's affidavit, so the application is ready to file as soon as the waiting period ends.",
        ],
      },
      {
        heading: "The hold-ups I see most often",
        paragraphs: [
          "If the registry isn't satisfied with something, it sends a requisition, which is a written list of things to fix before the grant will issue. The usual causes are:",
        ],
        bullets: [
          "The person's name is different on the will and the death certificate",
          "Handwritten changes or marks on the will that nobody has explained",
          "A staple mark or page that suggests something was attached and later removed",
          "An affidavit that was signed or witnessed incorrectly",
          "A will signed close to death where the death certificate mentions a condition like dementia",
        ],
      },
      {
        heading: "Dealing with them up front",
        paragraphs: [
          "Every one of those can usually be explained in a short extra affidavit filed with the application. It's far quicker to deal with them at the start than to wait for a requisition and then go back to the court.",
        ],
      },
      {
        heading: "After the grant",
        paragraphs: [
          "The grant gives you authority to deal with the estate. It doesn't finish the job. You still collect the assets, pay the debts, sort out tax and keep accounts before anything goes to the beneficiaries.",
          "Be careful with timing. In Queensland, someone who wants to make a family provision claim generally has to give notice within six months of the death and start proceedings within nine months. Distributing too early can leave you personally exposed, so get advice before paying anyone out.",
        ],
      },
    ],
    links: [
      { to: "/probate", label: "Probate on the Sunshine Coast" },
      { to: "/guides/$slug", label: "How to apply for probate in Queensland: step by step", params: { slug: "how-to-apply-for-probate-in-queensland" } },
      { to: "/guides/$slug", label: "Probate notices in Queensland", params: { slug: "probate-notices-queensland" } },
      { to: "/probate-queensland", label: "Probate in Queensland explained" },
    ],
    cta: "Been named executor? Tell us a little about the estate and we'll explain the next step and our fixed fee.",
    disclaimer: "This is general information about Queensland law, not legal advice about your situation.",
    sources: [
      { label: "Uniform Civil Procedure Rules 1999 (Qld), Chapter 15 (probate and administration)", url: "https://www.legislation.qld.gov.au/view/html/inforce/current/sl-1999-0111" },
      { label: "Succession Act 1981 (Qld), including Part 4 (family provision)", url: "https://www.legislation.qld.gov.au/view/html/inforce/current/act-1981-069" },
      { label: "Queensland Law Reporter (published notices)", url: "https://www.qlr.com.au/" },
    ],
  },
];

/** Posts awaiting the firm's approval. Remove a slug to publish that post. */
export const DRAFT_POST_SLUGS: string[] = [
  "conveyancing-costs-sunshine-coast-2026",
  "buying-a-unit-kings-beach-body-corporate",
];

export const isDraftPost = (slug: string) => DRAFT_POST_SLUGS.includes(slug);
export const getPost = (slug: string) => BLOG_POSTS.find((p) => p.slug === slug);
export const publishedPosts = () => BLOG_POSTS.filter((p) => !isDraftPost(p.slug));
